import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types';
import Page from "components/Page";
import useSearch from "hooks/useSearch";
import {useDidUpdateEffect} from "hooks/useDidUpdateEffect";
import Pagination from "components/Pagination";
import GridBlock from "components/GridBlock";
import Tr from "translations/Translations";
import SimpleTable from "components/complex/simpleTable/SimpleTable";
import WorkableIconPreviewButton from "components/button/WorkableIconPreviewButton";
import BoldTypography from "components/BoldTypography";
import {makeStyles} from "@material-ui/core";
import moment from "moment";
import DateFormats from "constants/DateFormats";
import Loading from "components/Loading";
import Workspace from "components/Workspace";
import RouterPath from "libs/RouterPath";
import Button from "components/Button";
import PurchasingGroupTemplateService from "services/PurchasingGroupTemplateService";
import BasicSearch from "components/BasicSearch";
import WorkableIconSearchButton from "components/button/WorkableIconSearchButton";
import EditDescriptionPopup, {createEditDescriptionFile} from "components/popup/EditDescriptionPopup";
import WorkableIconDeactivateButton from "components/button/WorkableIconDeactivateButton";
import WorkableIconActivateButton from "components/button/WorkableIconActivateButton";
import WorkableIconDeleteButton from "components/button/WorkableIconDeleteButton";
import WorkableIconDuplicateButton from "components/button/WorkableIconDuplicateButton";
import useSnackbar from "hooks/useSnackbar";
import {simpleConfirm} from "libs/ConfirmNotification";

const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(2)
    },
    dateTimeColumn: {
        width: 160,
    },
    buttonColumn: theme.components.table.buttonColumn,
    statusColumn: {
        width: 100
    },
    nameColumn: {
        wordBreak: "break-all",
        paddingRight: theme.spacing(2),
    },
    error: {
        color: theme.palette.error.main
    },
    success: {
        color: theme.palette.success.main
    }
}));

function PurchasingGroupTemplatesList(
    {
        match
    }
) {
    const classes = useStyles();
    const {searchData, totalCount, setTotalCount, setPaginationConfig, setSearchConfig, setSortConfig} = useSearch({
        sort: {
            column: 'dateInserted',
            direction: 'desc',
        }
    });
    const [templatesList, setTemplatesList] = useState(false);
    const [hasEdit, setHasEdit] = useState(false);
    const [working, setWorking] = useState();
    const [editDescriptionFile, setEditDescriptionFile] = useState(false);
    const [workingIds, setWorkingIds] = useState({});
    const {enqueueSuccessSnackbar} = useSnackbar();

    useEffect(() => {
        setWorking(true);
        PurchasingGroupTemplateService.getTemplatesList(searchData).then((resp) => {
            setTotalCount(resp.totalCount)
            setTemplatesList(resp);
        }).finally(() => setWorking(false));
        PurchasingGroupTemplateService.getTemplateListHasEdit().then(resp => setHasEdit(resp.success && resp.result));
        // eslint-disable-next-line
    }, [searchData]);

    useDidUpdateEffect(() => {
        if (templatesList?.totalCount !== undefined) {
            setTotalCount(templatesList.totalCount)
        }
    }, [templatesList]);

    const goToTpl = (x) => {
        const path = RouterPath.createPath(RouterPath.purchasingGroup.MP_PURCHASING_GROUP_TEMPLATES_TEMPLATE_ID, {
            id: x.identity
        })
        RouterPath.redirect(path)
    }

    const handleSortChange = (id) => {
        let direction = 'desc';
        const active = searchData.sort.column === id;
        if (active) {
            direction = searchData.sort.direction === 'asc' ? 'desc' : 'asc';
        }

        setSortConfig({
            column: id,
            direction
        })
    }

    const getSortProps = (id) => {
        return {
            active: searchData.sort.column === id,
            direction: searchData.sort.direction,
            onClick: () => {
                handleSortChange(id)
            }
        }
    }

    const onEditDescriptionFile = (row) => {
        setEditDescriptionFile(createEditDescriptionFile(0, 0,
            row.name, row.description || "", true, false, false))
    }

    const getHeaders = () => {
        const headers = [];
        headers.push({
            value: Tr('name'),
            sort: getSortProps('name')
        });
        headers.push(Tr('description'));
        headers.push({
            value: Tr('modyfication_date'),
            sort: getSortProps('dateModified')
        });
        headers.push({
            value: Tr('status'),
            sort: getSortProps('active')
        });
        headers.push('');
        headers.push('');
        headers.push('');
        headers.push('');
        return headers;
    };

    const setWorkingId = (row, action) => setWorkingIds({...workingIds, ["_" + row.identity]: action});
    const clearWorkingId = (row) => {
        const ids = {...workingIds};
        delete ids["_" + row.identity];
        setWorkingIds(ids)
    }

    const doDelete = (row) => {
        simpleConfirm("confirm_delete_template", () => {
            setWorkingId(row, "delete")
            PurchasingGroupTemplateService.deleteTemplate(row.identity).then(() => {
                setPaginationConfig(searchData.pagination);
            }).finally(() => clearWorkingId(row))
        })
    }

    const doActivate = (row) => {
        setWorkingId(row, "activate")
        PurchasingGroupTemplateService.activateTemplate(row.identity).then((resp) => {
            setTemplatesList({...templatesList, items: templatesList.items.map(x => x.identity === resp.identity ? resp : x)})
        }).finally(() => clearWorkingId(row))
    }
    const doDeactivate = (row) => {
        setWorkingId(row, "activate")
        PurchasingGroupTemplateService.deactivateTemplate(row.identity).then((resp) => {
            setTemplatesList({...templatesList, items: templatesList.items.map(x => x.identity === resp.identity ? resp : x)})
        }).finally(() => clearWorkingId(row))
    }
    const doClone = (row) => {
        setWorkingId(row, "clone")
        PurchasingGroupTemplateService.cloneTemplate(row.identity).then(resp => {
            if (resp && resp.success) {
                enqueueSuccessSnackbar(Tr('saved'))
                RouterPath.redirect(RouterPath.createPath(RouterPath.purchasingGroup.MP_PURCHASING_GROUP_TEMPLATES_TEMPLATE_ID, {
                    id: resp.result
                }))
            }
        }).finally(() => clearWorkingId(row))
    }

    return (<Page className={classes.root}>
        <Workspace
            title={Tr('purchase_groups_templates')}
        >
            {
                !templatesList ? <Loading/> :
                    <>
                        <GridBlock justify='space-between'>
                            <BasicSearch doSearch={setSearchConfig} searchInProgress={working} label={Tr('search2')}/>
                            <Button
                                disabled={!hasEdit}
                                onClick={() => {
                                    goToTpl({identity: 0})
                                }}
                            >
                                {Tr("purchase_groups_templates_add")}
                            </Button>
                        </GridBlock>

                        <SimpleTable
                            headers={
                                getHeaders()
                            }
                            items={
                                templatesList.items.map((x, i) => {
                                    return [
                                        x.name,
                                        <WorkableIconSearchButton
                                            onClick={() => onEditDescriptionFile(x)}
                                            tooltip={x.description ? <p className={classes.descriptionTooltip}>{x.description}</p> :
                                                <i>{Tr('no_description')}</i>}
                                        />,
                                        moment(x.dateModified).format(DateFormats.DATETIME_FORMAT),
                                        Tr(x.active ? 'status_active' : 'status_inactive'),
                                        x.active ?
                                            <WorkableIconDeactivateButton className={classes.success} onClick={() => doDeactivate(x)}
                                                                          working={workingIds["_" + x.identity] === "activate"}
                                                                          disabled={!x.hasEdit || !!workingIds["_" + x.identity]}/> :
                                            <WorkableIconActivateButton className={classes.error} onClick={() => doActivate(x)}
                                                                        working={workingIds["_" + x.identity] === "activate"}
                                                                        disabled={!x.hasEdit || !!workingIds["_" + x.identity]}/>,
                                        <WorkableIconDeleteButton onClick={() => doDelete(x)} working={workingIds["_" + x.identity] === "delete"}
                                                                  disabled={x.primary || !x.hasEdit || !!workingIds["_" + x.identity]}/>,
                                        <WorkableIconDuplicateButton onClick={() => doClone(x)} working={workingIds["_" + x.identity] === "clone"}
                                                                     disabled={!x.hasEdit || !!workingIds["_" + x.identity]}/>,
                                        <WorkableIconPreviewButton tooltip={Tr('details')} onClick={() => goToTpl(x)}
                                                                   disabled={!!workingIds["_" + x.identity]}/>,
                                    ]
                                })
                            }
                            columnClasses={
                                [
                                    classes.nameColumn,
                                    classes.buttonColumn,
                                    classes.dateTimeColumn,
                                    classes.statusColumn,
                                    classes.buttonColumn,
                                    classes.buttonColumn,
                                    classes.buttonColumn,
                                    classes.buttonColumn,
                                ]
                            }
                        />
                        {
                            !templatesList.items.length && <BoldTypography align='center' variant="h6">{Tr('no_options')}</BoldTypography>
                        }
                        <Pagination config={searchData.pagination} setConfig={setPaginationConfig} totalCount={totalCount} disabled={working}/>

                        {Boolean(editDescriptionFile) &&
                            <EditDescriptionPopup
                                editDescriptionFile={editDescriptionFile}
                                onUpdated={() => setEditDescriptionFile(null)}
                                onClose={() => setEditDescriptionFile(null)}
                                service={{
                                    updateDescription: async () => {
                                    }
                                }}
                            />
                        }
                    </>
            }
        </Workspace>
    </Page>)
}

PurchasingGroupTemplatesList.propTypes = {
    classes: PropTypes.any,
}

export default PurchasingGroupTemplatesList;